import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

import aguid from 'lib/aguid';

import GridContext from '../contexts/GridContext';
import BaseViewsContext from '../contexts/BaseViewsContext';
import DatasetDefinitionsContext from '../contexts/DatasetDefinitionsContext';
import getBaseViewFields from '../components/Grid/getBaseViewFields';
import getFieldType from '../utils/getFieldType';
import Flash from '../components/Flash';
import CommitmentQueryContext from '../contexts/CommitmentQueryContext';

const GridProvider = ({
  children,
  dataType,
  metricFiltering,
  dateScopeOverride,
  gridQueryOverride,
  gridCountQueryOverride,
  gridTotalsQueryOverride,
  baseViewOverride,
  sortOverride,
  titleOverride,
  gridApi,
  setGridApi,
}: {
  children: JSX.Element | JSX.Element[];
  dataType: string;
  metricFiltering?: FilterInput;
  dateScopeOverride?: DateRangeInput;
  gridQueryOverride?: string;
  gridCountQueryOverride?: string;
  gridTotalsQueryOverride?: string;
  baseViewOverride?: FleetOps.BaseView;
  sortOverride?: SimpleGridSort[];
  titleOverride?: string;
  gridApi?: GridApi;
  setGridApi?: React.Dispatch<React.SetStateAction<GridApi | undefined>>;
}) => {
  const { laneCommitmentsGridKey } = useContext(CommitmentQueryContext);
  const [internalGridApi, setInternalGridApi] = useState<GridApi>();
  const [gridId, setGridId] = useState<string>(
    laneCommitmentsGridKey ? laneCommitmentsGridKey : aguid(),
  );
  const [currentGroup, setCurrentGroup] = useState<string | undefined>();
  const [isFlashVisible, setIsFlashVisible] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [sort, setSort] = useState<SimpleGridSort[]>([]);
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();
  const { baseViews } = useContext(BaseViewsContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const baseView = baseViewOverride ? baseViewOverride : baseViews[dataType];

  const restartGrid = useCallback((noFlash?: boolean) => {
    setGridId(aguid());
    if (noFlash) {
      return;
    }
    setIsFlashVisible(true);
  }, []);

  useEffect(() => {
    if (laneCommitmentsGridKey && gridId !== laneCommitmentsGridKey) {
      setGridId(laneCommitmentsGridKey);
    }
  }, [gridId, laneCommitmentsGridKey]);

  const groups = baseView
    ? getBaseViewFields(baseView)
        .filter((fv) => {
          const fieldType = getFieldType({
            datasets,
            datasetType: fv.dataset || baseView.type,
            fieldName: fv.field,
          });
          return fieldType === 'text';
        })
        .map((fv) => fv.field)
    : [];

  useEffect(() => {
    if (!sortOverride || !gridApi) {
      return;
    }

    gridApi.applyColumnState({
      state: sortOverride.map((sort) => ({
        colId: sort.field,
        sort: sort.direction,
      })),
      defaultState: { sort: null },
    });

    return () => {
      if (gridApi.isDestroyed()) {
        return;
      }

      gridApi.applyColumnState({
        state: sortOverride.map((sort) => ({
          colId: sort.field,
          sort: sort.direction,
        })),
        defaultState: { sort: null },
      });
    };
  }, [gridApi, sortOverride]);

  return (
    <GridContext.Provider
      value={{
        gridId,
        gridApi: gridApi ? gridApi : internalGridApi,
        setGridApi: setGridApi ? setGridApi : setInternalGridApi,
        dataType,
        groups,
        currentGroup,
        setCurrentGroup,
        metricFiltering,
        dateScopeOverride,
        gridQueryOverride,
        gridCountQueryOverride,
        gridTotalsQueryOverride,
        baseViewOverride,
        sortOverride,
        titleOverride: titleOverride
          ? titleOverride
          : baseView
            ? baseView.nameAlias
            : undefined,
        sort,
        setSort,
        restartGrid,
        isLoadingData,
        setIsLoadingData,
        exportHandle,
        setExportHandle,
      }}
    >
      <div style={{ width: '100%', height: '100%' }}>
        <Flash
          isFlashVisible={isFlashVisible}
          setIsFlashVisible={setIsFlashVisible}
          type="info"
          message="Your search session has expired, you will need to start scrolling from the beginning again. We are sorry for the inconvenience."
        />
        {children}
      </div>
    </GridContext.Provider>
  );
};

export default GridProvider;
